import React, { useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import AuthContext from "../Context/AuthContext";

function StepCard({ number, title, description, children }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 h-8 w-8 rounded-full bg-blue-600 text-white text-sm font-bold grid place-items-center">
        {number}
      </div>
      <div className="flex-1 pb-6 border-b border-slate-100 last:border-0">
        <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
        {description && <p className="text-sm text-slate-500 mb-3">{description}</p>}
        {children}
      </div>
    </div>
  );
}

function Badge({ children, color = "blue" }) {
  const colors = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    green: "bg-green-50 text-green-700 border-green-100",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${colors[color]}`}>
      {children}
    </span>
  );
}

export default function HowToUse() {
  const history = useHistory();
  const location = useLocation();
  const { userData } = useContext(AuthContext);

  const user = userData?.user?.user;
  const role = user?.role || "User";

  const tabsAdmin = [
    { key: "accounts", label: "Accounts", path: "/accounts" },
    { key: "guidelines", label: "Guidelines", path: "/guidelines" },
    { key: "yourteam", label: "Your Team", path: "/yourteam" },
    { key: "howto", label: "How to Use", path: "/howto" },
  ];
  const tabsUser = [
    { key: "guidelines", label: "Guidelines", path: "/guidelines" },
    { key: "howto", label: "How to Use", path: "/howto" },
  ];
  const tabs = role === "Admin" ? tabsAdmin : tabsUser;

  const activeKey =
    tabs.find((t) => location.pathname.startsWith(`/${t.key}`))?.key || "howto";

  function go(path) {
    history.push(path);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-800">
      {/* Üst Bar */}
      <div className="sticky top-0 z-10 bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/50 border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex items-center py-3">
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
              {tabs.map((t) => {
                const active = activeKey === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => go(t.path)}
                    className={[
                      "px-4 py-2 rounded-lg text-sm font-semibold transition",
                      active
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
                    ].join(" ")}
                    aria-current={active ? "page" : undefined}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* İçerik */}
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-10">
        {/* Başlık */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">How to Use</h1>
          <p className="text-slate-500 text-sm">
            UM Ad Compliance Tool'u nasıl kullanacağınıza dair adım adım kılavuz.
          </p>
        </div>

        {/* Bölüm 1 — Web Uygulaması */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-9 w-9 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
              <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0H3" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold">Web Uygulaması</h2>
              <p className="text-xs text-slate-500">Guideline yönetimi ve kampanya takibi</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-1">
            <StepCard
              number="1"
              title="Giriş Yapın"
              description="E-posta adresiniz ve şifrenizle sisteme giriş yapın. Admin tarafından oluşturulmuş hesabınızla erişim sağlayabilirsiniz."
            />

            <StepCard
              number="2"
              title="Guidelines Sekmesine Gidin"
              description="Üst menüden Guidelines'a tıklayın. İki alt sekme göreceksiniz:"
            >
              <div className="flex flex-wrap gap-2 mt-1">
                <Badge color="blue">Account Guidelines</Badge>
                <Badge color="blue">Campaign Guidelines</Badge>
              </div>
              <p className="text-sm text-slate-500 mt-2">
                <b>Account Guidelines</b> — hesabınıza bağlı account kurallarını listeler. Satıra tıklayarak detayları görüntüleyebilir ve düzenleyebilirsiniz.
              </p>
              <p className="text-sm text-slate-500 mt-1">
                <b>Campaign Guidelines</b> — oluşturduğunuz kampanya kurallarını listeler. Kampanya adına tıklayarak bütçe, bütçe türü, A/B testi gibi alanları düzenleyebilir; hazır olduğunda <Badge color="green">Kampanya Kuruldu Mu ?</Badge> butonuyla onaylayabilirsiniz.
              </p>
            </StepCard>

            <StepCard
              number="3"
              title="Yeni Guideline Oluşturun"
              description="Sağ üstteki '+ New Guideline' butonuna tıklayın. Açılan formda:"
            >
              <ul className="mt-2 space-y-1.5 text-sm text-slate-500 list-disc pl-4">
                <li><b>Method</b> olarak <Badge color="slate">Account</Badge> veya <Badge color="slate">Campaign</Badge> seçin.</li>
                <li><b>Platform</b> seçin (Google, Meta, LinkedIn).</li>
                <li>Campaign seçtiyseniz kampanya adını girin ve ilgili <b>account'u</b> seçin.</li>
                <li>Uygulamak istediğiniz <b>kuralları</b> etiketlerden seçin, değerleri doldurun.</li>
                <li><b>Submit</b> ile kaydedin.</li>
              </ul>
            </StepCard>

            {role === "Admin" && (
              <StepCard
                number="4"
                title="Ekip & Hesap Yönetimi (Admin)"
                description="Accounts ve Your Team sekmeleri üzerinden:"
              >
                <ul className="mt-2 space-y-1.5 text-sm text-slate-500 list-disc pl-4">
                  <li>Yeni hesap ve platform ekleyebilir, düzenleyebilir veya silebilirsiniz.</li>
                  <li>Yeni kullanıcı (member) ve admin oluşturabilir, hesap atayabilirsiniz.</li>
                </ul>
              </StepCard>
            )}
          </div>
        </section>

        {/* Bölüm 2 — Chrome Extension */}
        <section>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-9 w-9 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
              <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold">Chrome Extension</h2>
              <p className="text-xs text-slate-500">Reklam platformlarında anlık kural kontrolü</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-1">
            <StepCard
              number="1"
              title="Extension'ı Yükleyin"
              description="Chrome Web Store'dan UM Ad Compliance Tool extension'ını tarayıcınıza ekleyin."
            >
              <a
                href="https://chromewebstore.google.com/detail/um-ad-compliance-tool/hijfehjckpjijmcnbfpckaibnbkmlgfg"
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 transition"
              >
                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
                Chrome Web Store'da Aç
              </a>
            </StepCard>

            <StepCard
              number="2"
              title="Giriş Yapın"
              description="Extension'ı açtığınızda web uygulamasıyla aynı kullanıcı adı ve şifreyle giriş yapın. Oturum açık kaldığı sürece tekrar giriş gerekmez."
            />

            <StepCard
              number="3"
              title="Reklam Platformunu Açın"
              description="Google Ads, Meta Ads Manager veya LinkedIn Campaign Manager'ı Chrome'da açın."
            />

            <StepCard
              number="4"
              title="Kampanya veya Account Seçin"
              description="Kontrol etmek istediğiniz kampanyayı veya hesabı platform üzerinde açın, ardından extension ikonuna tıklayın."
            />

            <StepCard
              number="5"
              title="Compliance Kontrolünü Çalıştırın"
              description="Extension, o an açık olan sayfayı Guidelines'daki kurallarla karşılaştırır ve uyumsuzlukları size listeler."
            />
          </div>
        </section>

        {/* İpuçları */}
        <section>
          <h2 className="text-base font-bold mb-4">İpuçları</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                ),
                title: "Kural çakışması",
                desc: "Aynı account + açıklama için farklı bir kural eklenirse sistem sizi uyarır. Mevcut kuralı güncelleyin ya da önce silin.",
              },
              {
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                ),
                title: "Kampanya Kuruldu",
                desc: "Kampanya hazırlandıktan sonra Campaign Guidelines'dan ilgili kampanyaya girerek 'Kampanya Kuruldu' butonuna basın.",
              },
              
            ].map((tip) => (
              <div key={tip.title} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex gap-3">
                <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <svg className="h-4 w-4 text-slate-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    {tip.icon}
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{tip.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
